import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import TableChartIcon from '@mui/icons-material/TableChart';
// Use the styled fork of SheetJS so banners / fills / borders survive the
// write step. Plain `xlsx` v0.18 drops every `.s` property silently.
import { createStyledWorkbook, appendStyledSheet, saveStyledWorkbook } from '../../utils/xlsxStyled';
import api, { apiBaseURL } from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay, formatDateInput, formatDateTimeDisplay, todayDateInput } from '../../utils/dateUtils';
import { hasActionPermission } from '../../utils/accessControl';
import '../../App.css';

const today = todayDateInput();
const yearStart = `${new Date().getFullYear()}-01-01`;
const formatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const DEFAULT_REPORT_OPTIONS = {
  summary: true,
  team: true,
  entries: true,
  championships: true,
  awards: true,
  scores: true,
  fees: true,
};
const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const classScoreFields = ['score_type', 'score_head_neck', 'score_body', 'score_legs', 'score_movement'];

const blankEvent = {
  event_name: '',
  organizer: '',
  category_id: '',
  ecaho_category: '',
  start_date: today,
  end_date: today,
  start_time: '',
  end_time: '',
  event_timezone: 'Asia/Riyadh',
  city: '',
  event_address: '',
  contact_phone: '',
  event_info_url: '',
  documents_url: '',
  judging_system: 'ECAHO_20_POINTS',
  championship_method: 'ECAHO_4_2_1_MEDALS',
  trainers: [],
  grooms: [],
  notes: '',
};

const blankClass = {
  class_name: '',
  class_name_ar: '',
  class_code: '',
  class_date: '',
  start_time: '',
  championship_group: '',
  sex: 'Mixed',
  min_age_months: '',
  max_age_months: '',
  sort_order: 0,
};

const blankEntry = {
  entry_id: '',
  class_id: '',
  horse_id: '',
  catalogue_number: '',
  handler_id: '',
  handler_name: '',
  handler_fee: '',
  handler_fee_paid_by: 'Owner',
  registration_fee_amount: '',
  registration_fee_currency: 'SAR',
  registration_fee_payer: 'Owner',
  result_notes: '',
};

const blankAward = {
  award_scope: 'Class',
  class_id: '',
  championship_id: '',
  entry_id: '',
  championship_result_id: '',
  award_name: '',
  award_name_ar: '',
  placement: '',
  prize_amount: '',
  prize_currency: 'SAR',
  prize_recipient: 'Owner',
  notes: '',
};

const blankEntryResult = {
  entry_id: '',
  class_rank: '',
  qualified_for_championship: 0,
  score_type: '',
  score_head_neck: '',
  score_body: '',
  score_legs: '',
  score_movement: '',
  score_total: '',
};

const blankCategory = {
  category_name_en: '',
  category_name_ar: '',
  sort_order: 100,
};

const blankChampionship = {
  championship_name: '',
  championship_name_ar: '',
  championship_group: '',
  sort_order: 0,
};

const blankResult = {
  championship_id: '',
  entry_id: '',
  medal: 'Gold',
  final_rank: '',
  gold_votes: '',
  silver_votes: '',
  bronze_votes: '',
  championship_points: '',
  handler_fee: '',
  handler_fee_paid_by: 'Owner',
  prize_amount: '',
  prize_received_by: 'Owner',
  notes: '',
};

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

function tLabels(lang) {
  const ar = lang === 'ar';
  return {
    title: ar ? 'بطولات جمال الخيل' : 'Arabian horse shows',
    hint: ar
      ? 'أنشئ البطولة أولاً، ثم أضف الكلاسات والخيل المشاركة ونتائج البطولة النهائية.'
      : 'Create the show first, then add classes, entries, and championship medal results.',
    addEvent: ar ? 'إضافة بطولة' : 'Add show',
    editEvent: ar ? 'تعديل البطولة' : 'Edit show',
    addClass: ar ? 'إضافة شوط' : 'Add class',
    editClass: ar ? 'تعديل الشوط' : 'Edit class',
    addEntry: ar ? 'إضافة خيل مشارك' : 'Add entry',
    editEntry: ar ? 'تعديل مشاركة الخيل' : 'Edit entry',
    addCategory: ar ? 'إضافة تصنيف' : 'Add category',
    addAward: ar ? 'توثيق جائزة' : 'Record award',
    editAward: ar ? 'تعديل الجائزة' : 'Edit award',
    deleteAward: ar ? 'حذف الجائزة' : 'Delete award',
    recordClassResult: ar ? 'تسجيل نتيجة الكلاس' : 'Record class result',
    copyToChampionship: ar ? 'نقله للنهائي' : 'Copy to championship',
    addMedalHere: ar ? 'تسجيل النتائج' : 'Record results',
    setMedal: ar ? 'تسجيل النتائج' : 'Record results',
    pending: ar ? 'بانتظار النتيجة' : 'Pending',
    addChampionship: ar ? 'إنشاء نهائي' : 'Create championship',
    addResult: ar ? 'تسجيل النتائج' : 'Record results',
    refresh: ar ? 'تحديث' : 'Refresh',
    exportPdf: ar ? 'تقرير PDF' : 'PDF report',
    exportExcel: ar ? 'تقرير Excel' : 'Excel report',
    exportAllPdf: ar ? 'PDF للبطولات' : 'All shows PDF',
    exportAllExcel: ar ? 'Excel للبطولات' : 'All shows Excel',
    reportOptions: ar ? 'خيارات التقرير' : 'Report options',
    reportOptionsHint: ar ? 'اختر الأقسام التي تريد ظهورها في الوثيقة قبل الاستخراج.' : 'Choose the sections to include before generating the document.',
    includeSummary: ar ? 'ملخص البطولة' : 'Show summary',
    includeTeam: ar ? 'المدربون والساسة' : 'Trainers and grooms',
    includeEntries: ar ? 'المشاركات والكلاسات' : 'Classes and entries',
    includeChampionships: ar ? 'النهائيات والنتائج' : 'Championship results',
    includeAwards: ar ? 'الجوائز' : 'Awards',
    includeScores: ar ? 'درجات التقييم' : 'Scores',
    includeFees: ar ? 'رسوم التسجيل' : 'Registration fees',
    generateReport: ar ? 'استخراج التقرير' : 'Generate report',
    reportGeneratedAt: ar ? 'وقت استخراج التقرير' : 'Report generated at',
    reportScope: ar ? 'نطاق التقرير' : 'Report scope',
    selectedShow: ar ? 'البطولة المحددة' : 'Selected show',
    filteredShows: ar ? 'البطولات المعروضة' : 'Filtered shows',
    owner: ar ? 'المالك' : 'Owner',
    fees: ar ? 'الرسوم' : 'Fees',
    payer: ar ? 'الدافع' : 'Payer',
    prize: ar ? 'الجائزة' : 'Prize',
    recipient: ar ? 'المستلم' : 'Recipient',
    from: ar ? 'من' : 'From',
    to: ar ? 'إلى' : 'To',
    search: ar ? 'بحث باسم البطولة أو المدينة' : 'Search show or city',
    eventName: ar ? 'اسم البطولة' : 'Show name',
    organizer: ar ? 'المنظم' : 'Organizer',
    category: ar ? 'تصنيف ECAHO' : 'ECAHO category',
    trainers: ar ? 'المدربون' : 'Trainers',
    grooms: ar ? 'الساسة' : 'Grooms',
    handler: ar ? 'العارض' : 'Handler',
    addTrainer: ar ? 'إضافة مدرب' : 'Add trainer',
    addGroom: ar ? 'إضافة سايس' : 'Add groom',
    addHandler: ar ? 'إضافة عارض' : 'Add handler',
    personName: ar ? 'الاسم' : 'Name',
    registrationFee: ar ? 'رسوم التسجيل' : 'Registration fee',
    feePayer: ar ? 'دافع الرسوم' : 'Fee payer',
    handlerFee: ar ? 'أتعاب العارض' : lang === 'pt' ? 'Taxa do apresentador' : 'Handler fee',
    handlerFeePaidBy: ar ? 'دافع أتعاب العارض' : lang === 'pt' ? 'Taxa do apresentador paga por' : 'Handler fee paid by',
    ownerPays: ar ? 'المالك' : 'Owner',
    stablePays: ar ? 'المربط' : 'Stable',
    awardName: ar ? 'اسم الجائزة' : 'Award name',
    prizeAmount: ar ? 'قيمة الجائزة' : 'Prize amount',
    prizeRecipient: ar ? 'مستلم الجائزة' : 'Prize recipient',
    prizeReceivedBy: ar ? 'مستلم قيمة الجائزة' : lang === 'pt' ? 'Prêmio recebido por' : 'Prize received by',
    feesFinance: ar ? 'الرسوم والمالية' : lang === 'pt' ? 'Taxas e finanças' : 'Fees and finance',
    awardScope: ar ? 'نوع الجائزة' : 'Award scope',
    classAward: ar ? 'جائزة شوط' : 'Class award',
    championshipAward: ar ? 'جائزة نهائي' : 'Championship award',
    categoryNameEn: ar ? 'اسم التصنيف بالإنجليزية' : 'Category EN',
    categoryNameAr: ar ? 'اسم التصنيف بالعربية' : 'Category AR',
    start: ar ? 'تاريخ البداية' : 'Start date',
    end: ar ? 'تاريخ النهاية' : 'End date',
    startTime: ar ? 'وقت البداية' : 'Start time',
    endTime: ar ? 'وقت النهاية' : 'End time',
    timezone: ar ? 'المنطقة الزمنية' : 'Timezone',
    address: ar ? 'العنوان' : 'Address',
    phone: ar ? 'الهاتف' : 'Phone',
    classDate: ar ? 'تاريخ الشوط' : 'Class date',
    classTime: ar ? 'وقت الشوط' : 'Class time',
    minAgeMonths: ar ? 'أقل عمر بالشهور' : 'Min age months',
    maxAgeMonths: ar ? 'أعلى عمر بالشهور' : 'Max age months',
    sortOrder: ar ? 'ترتيب العرض' : 'Sort order',
    city: ar ? 'المدينة' : 'City',
    notes: ar ? 'ملاحظات' : 'Notes',
    classes: ar ? 'الأشواط' : 'Classes',
    classesHint: ar ? 'أضف الأشواط أولاً، ثم أدخل الخيل تحت الشوط المناسب.' : 'Create classes first, then add horses inside the right class.',
    addHorseToClass: ar ? 'إضافة خيل لهذا الشوط' : 'Add horse to this class',
    noClasses: ar ? 'لا توجد أشواط بعد. ابدأ بإضافة شوط للبطولة.' : 'No classes yet. Start by adding a class to the show.',
    entries: ar ? 'الخيل المشاركة' : 'Entries',
    championships: ar ? 'النهائيات' : 'Championships',
    medals: ar ? 'الميداليات' : 'Medals',
    actions: ar ? 'إجراءات' : 'Actions',
    className: ar ? 'اسم الشوط' : 'Class name',
    classCode: ar ? 'رقم/رمز الشوط' : 'Class code',
    group: ar ? 'مجموعة النهائي' : 'Championship group',
    sex: ar ? 'الجنس' : 'Sex',
    horse: ar ? 'الخيل' : 'Horse',
    catalog: ar ? 'رقم الكتالوج' : 'Catalogue no.',
    rank: ar ? 'ترتيب الشوط' : 'Class rank',
    qualified: ar ? 'مؤهل للنهائي' : 'Qualified',
    score: ar ? 'الدرجة' : 'Score',
    type: ar ? 'النوع العام' : 'Type',
    headNeck: ar ? 'الرأس والرقبة' : 'Head & neck',
    body: ar ? 'الجسم' : 'Body',
    legs: ar ? 'القوائم' : 'Legs',
    movement: ar ? 'الحركة' : 'Movement',
    total: ar ? 'الإجمالي' : 'Total',
    resultNotes: ar ? 'ملاحظة' : 'Note',
    championshipName: ar ? 'اسم النهائي' : 'Championship name',
    medal: ar ? 'الميدالية' : 'Medal',
    gold: ar ? 'ذهبية' : 'Gold',
    silver: ar ? 'فضية' : 'Silver',
    bronze: ar ? 'برونزية' : 'Bronze',
    other: ar ? 'أخرى' : 'Other',
    participant: ar ? 'شارك بدون نتيجة' : 'Participant',
    fourth: ar ? 'مركز آخر' : 'Other place',
    goldVotes: ar ? 'أصوات الذهب' : 'Gold votes',
    silverVotes: ar ? 'أصوات الفضة' : 'Silver votes',
    bronzeVotes: ar ? 'أصوات البرونز' : 'Bronze votes',
    points: ar ? 'نقاط النهائي' : 'Championship points',
    male: ar ? 'ذكور' : 'Male',
    female: ar ? 'إناث' : 'Female',
    mixed: ar ? 'مختلط' : 'Mixed',
    save: ar ? 'حفظ' : 'Save',
    cancel: ar ? 'إلغاء' : 'Cancel',
    noEvents: ar ? 'لا توجد بطولات ضمن الفلاتر الحالية.' : 'No shows in the selected filters.',
    saveSuccess: ar ? 'تم حفظ بيانات البطولة بنجاح.' : lang === 'pt' ? 'Dados da competição salvos com sucesso.' : 'Competition data saved successfully.',
    saveError: ar ? 'تعذر حفظ بيانات البطولة. تحقق من الحقول المطلوبة وحاول مرة أخرى.' : lang === 'pt' ? 'Não foi possível salvar os dados da competição. Verifique os campos obrigatórios e tente novamente.' : 'Could not save competition data. Check the required fields and try again.',
    loadError: ar ? 'تعذر تحميل بيانات البطولات.' : lang === 'pt' ? 'Não foi possível carregar as competições.' : 'Failed to load competitions.',
    copyChampionshipError: ar ? 'تعذر نقل المشاركة إلى النهائي.' : lang === 'pt' ? 'Não foi possível copiar a inscrição para o campeonato.' : 'Failed to copy entry to championship.',
    missingChampionshipGroup: ar ? 'مجموعة النهائي غير محددة لهذا الشوط.' : lang === 'pt' ? 'O grupo do campeonato não está definido para esta classe.' : 'Missing championship group for this class.',
  };
}

function fmtDate(value) {
  return formatDateDisplay(value);
}

function dateInputValue(value) {
  return formatDateInput(value);
}

function horseName(horse, lang) {
  if (!horse) return '-';
  return lang === 'ar'
    ? horse.name_arabic || horse.horse_name_arabic || horse.name || horse.horse_name || '-'
    : horse.name || horse.horse_name || horse.name_arabic || horse.horse_name_arabic || '-';
}

function className(row, lang) {
  return lang === 'ar' ? row.class_name_ar || row.class_name : row.class_name || row.class_name_ar || '-';
}

function championshipName(row, lang) {
  if (!row) return '-';
  return lang === 'ar'
    ? row.championship_name_ar || row.championship_name || '-'
    : row.championship_name || row.championship_name_ar || '-';
}

function categoryName(row, lang) {
  if (!row) return '-';
  return lang === 'ar'
    ? row.category_name_ar || row.category_name_en || row.ecaho_category || '-'
    : row.category_name_en || row.category_name_ar || row.ecaho_category || '-';
}

function personLabel(option) {
  if (!option) return '';
  return typeof option === 'string' ? option : option.name || option.member_display_name || option.member_name || '';
}

function teamOption(member) {
  if (!member) return null;
  const name = personLabel(member);
  if (!name) return null;
  return {
    type: member.member_role || member.type,
    id: member.member_role === 'Trainer' ? member.trainer_id : member.member_role === 'Groom' ? member.groom_id : member.id,
    name,
  };
}

function timeInputValue(value) {
  if (!value) return '';
  return String(value).slice(0, 5);
}

function eventFormFromRecord(event) {
  if (!event) return copy(blankEvent);
  return {
    ...copy(blankEvent),
    ...event,
    category_id: event.category_id || '',
    start_date: dateInputValue(event.start_date),
    end_date: dateInputValue(event.end_date),
    start_time: timeInputValue(event.start_time),
    end_time: timeInputValue(event.end_time),
    event_timezone: event.event_timezone || 'Asia/Riyadh',
    trainers: (event.team_members || [])
      .filter((member) => member.member_role === 'Trainer')
      .map(teamOption)
      .filter(Boolean),
    grooms: (event.team_members || [])
      .filter((member) => member.member_role === 'Groom')
      .map(teamOption)
      .filter(Boolean),
  };
}

function toTeamMember(option, role) {
  const name = personLabel(option);
  if (!name) return null;
  const member = { member_role: role, member_name: name };
  if (typeof option !== 'string' && option?.id) {
    if (role === 'Trainer') member.trainer_id = option.id;
    if (role === 'Groom') member.groom_id = option.id;
  }
  return member;
}

function SearchCreateField({ label, value, options, multiple = false, onChange, onAdd }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Autocomplete
        multiple={multiple}
        options={options}
        value={value}
        getOptionLabel={personLabel}
        isOptionEqualToValue={(option, selected) => Number(option.id) === Number(selected.id) && option.type === selected.type}
        onChange={onChange}
        renderInput={(params) => <TextField {...params} label={label} />}
        sx={{ flex: 1 }}
      />
      <IconButton
        onClick={onAdd}
        sx={{
          mt: 0.5,
          width: 44,
          height: 44,
          border: `1px solid ${COLORS.line}`,
          color: COLORS.gold,
          bgcolor: COLORS.card,
        }}
      >
        <AddIcon />
      </IconButton>
    </Stack>
  );
}

function MetricCard({ title, value, icon, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: soft, color }}>{icon}</Avatar>
      </Stack>
    </Paper>
  );
}

function medalIcon(medal) {
  const colorMap = {
    Gold: COLORS.gold,
    Silver: COLORS.blue,
    Bronze: COLORS.warning,
  };
  if (!colorMap[medal]) return null;
  return <MilitaryTechIcon sx={{ color: colorMap[medal], fontSize: 20, verticalAlign: 'middle', mx: 0.5 }} />;
}

function safeText(value) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function escapeHtml(value) {
  return safeText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function moneyText(amount, currency = 'SAR') {
  const number = Number(amount);
  if (!Number.isFinite(number) || number <= 0) return '-';
  return `${formatter.format(number)} ${currency || 'SAR'}`;
}

function labelForMedal(medal, L) {
  const map = {
    Gold: L.gold,
    Silver: L.silver,
    Bronze: L.bronze,
    Other: L.other,
    Participant: L.participant,
    Fourth: L.fourth,
    Pending: L.pending,
  };
  return map[medal] || medal || '-';
}

function fileSafeName(value) {
  return safeText(value)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

function resolveAssetUrl(value) {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${apiBaseURL}${value.startsWith('/') ? '' : '/'}${value}`;
}

function eventDisplayName(event, lang) {
  if (!event) return '-';
  return lang === 'ar'
    ? event.event_name_ar || event.event_name || '-'
    : event.event_name || event.event_name_ar || '-';
}

function buildReportRows(eventsToExport, lang, L, options = DEFAULT_REPORT_OPTIONS) {
  const summary = [];
  const team = [];
  const classes = [];
  const entries = [];
  const championships = [];
  const championshipResults = [];
  const awards = [];

  eventsToExport.forEach((event) => {
    const eventName = eventDisplayName(event, lang);
    const common = {
      [L.eventName]: eventName,
      [L.category]: categoryName(event, lang),
      [L.city]: event.city || '',
      [L.start]: fmtDate(event.start_date),
      [L.end]: fmtDate(event.end_date),
    };

    summary.push({
      ...common,
      [L.organizer]: event.organizer || '',
      [L.classes]: event.classes?.length || 0,
      [L.entries]: event.entries?.length || 0,
      [L.championships]: event.championships?.length || 0,
      [L.medals]: event.championship_results?.length || 0,
      [L.addAward]: event.awards?.length || 0,
      [L.notes]: event.notes || '',
    });

    (event.team_members || []).forEach((member) => {
      team.push({
        ...common,
        [L.type]: member.member_role === 'Trainer' ? L.trainers : L.grooms,
        [L.personName]: member.member_display_name || member.member_name || '',
      });
    });

    (event.classes || []).forEach((row) => {
      classes.push({
        ...common,
        [L.className]: className(row, lang),
        [L.classCode]: row.class_code || '',
        [L.classDate]: fmtDate(row.class_date),
        [L.classTime]: row.start_time || '',
        [L.group]: row.championship_group || '',
        [L.sex]: row.sex || '',
      });
    });

    (event.entries || []).forEach((entry) => {
      const cls = (event.classes || []).find((row) => Number(row.class_id) === Number(entry.class_id));
      const entryRow = {
        ...common,
        [L.className]: className(cls || {}, lang),
        [L.catalog]: entry.catalogue_number || '',
        [L.horse]: horseName(entry, lang),
        [L.owner]: entry.owner_name || '',
        [L.handler]: entry.handler_display_name || entry.handler_name || '',
        [L.rank]: entry.class_rank || '',
        [L.qualified]: Number(entry.qualified_for_championship) ? L.qualified : '',
        [L.notes]: entry.result_notes || '',
      };
      if (options.scores) {
        entryRow[L.score] = entry.score_total || '';
        entryRow[L.headNeck] = entry.score_head_neck || '';
        entryRow[L.body] = entry.score_body || '';
        entryRow[L.legs] = entry.score_legs || '';
        entryRow[L.movement] = entry.score_movement || '';
      }
      if (options.fees) {
        entryRow[L.fees] = moneyText(entry.registration_fee_amount, entry.registration_fee_currency);
      }
      entries.push(entryRow);
    });

    (event.championships || []).forEach((row) => {
      championships.push({
        ...common,
        [L.championshipName]: lang === 'ar' ? row.championship_name_ar || row.championship_name : row.championship_name || row.championship_name_ar,
        [L.group]: row.championship_group || '',
      });
    });

    (event.championship_results || []).forEach((result) => {
      const championship = (event.championships || []).find((row) => Number(row.championship_id) === Number(result.championship_id));
      championshipResults.push({
        ...common,
        [L.championshipName]: championship?.championship_name || result.championship_name || '',
        [L.horse]: horseName(result, lang),
        [L.handler]: result.handler_display_name || result.handler_name || '',
        [L.medal]: labelForMedal(result.medal, L),
        [L.rank]: result.final_rank || '',
        [L.goldVotes]: result.gold_votes || '',
        [L.silverVotes]: result.silver_votes || '',
        [L.bronzeVotes]: result.bronze_votes || '',
        [L.points]: result.championship_points || '',
        [L.notes]: result.notes || '',
      });
    });

    (event.awards || []).forEach((award) => {
      awards.push({
        ...common,
        [L.awardScope]: award.award_scope === 'Championship' ? L.championshipAward : L.classAward,
        [L.awardName]: lang === 'ar' ? award.award_name_ar || award.award_name : award.award_name || award.award_name_ar,
        [L.horse]: horseName(award, lang),
        [L.rank]: award.placement || '',
        [L.prize]: moneyText(award.prize_amount, award.prize_currency),
        [L.recipient]: award.prize_recipient === 'Stable' ? L.stablePays : L.ownerPays,
        [L.notes]: award.notes || '',
      });
    });
  });

  return { summary, team, classes, entries, championships, championshipResults, awards };
}

// ---------------------------------------------------------------------------
// Premium styled sheet builder
// ---------------------------------------------------------------------------
// Layout (row indices are 0-based):
//   row 0 : gold banner with `sheetName` merged across the columns
//   row 1 : subtitle (entity / date stamp) merged across
//   row 2 : table header row — gold fill, white bold text, borders
//   row 3+: data rows — alternating fills, thin borders, numbers right-aligned
//   last  : footer "Generated at ..." merged, italic muted
//
// Currency-looking columns get a `#,##0.00` number format; numeric columns
// keep their default. Detection is heuristic — header includes any of the
// keywords below.
// تنسيق التصدير موحّد عبر مساعد xlsxStyled (ExcelJS) — أُزيل البناء المحلّي المكرّر.
function appendSheet(workbook, sheetName, rows, subtitle = '') {
  return appendStyledSheet(workbook, sheetName, rows, subtitle);
}

async function exportEventsExcel(eventsToExport, lang, L, options = DEFAULT_REPORT_OPTIONS) {
  if (!eventsToExport.length) return false;
  const workbook = createStyledWorkbook();
  const rows = buildReportRows(eventsToExport, lang, L, options);
  // Subtitle reused across every sheet so the audience knows which events
  // produced the data without flipping back to the Summary tab.
  const eventLabel = eventsToExport.length === 1
    ? eventDisplayName(eventsToExport[0], lang)
    : `${eventsToExport.length} ${L?.events || 'events'}`;
  const subtitle = `${eventLabel} · ${today}`;

  if (options.summary)        appendSheet(workbook, 'Summary',        rows.summary,             subtitle);
  if (options.team)           appendSheet(workbook, 'Team',           rows.team,                subtitle);
  if (options.entries) {
    appendSheet(workbook, 'Classes',                                   rows.classes,            subtitle);
    appendSheet(workbook, 'Entries',                                   rows.entries,            subtitle);
  }
  if (options.championships) {
    appendSheet(workbook, 'Championships',                             rows.championships,      subtitle);
    appendSheet(workbook, 'Champ results',                             rows.championshipResults, subtitle);
  }
  if (options.awards)         appendSheet(workbook, 'Awards',         rows.awards,              subtitle);
  if (!workbook.worksheets.length) {
    appendSheet(workbook, 'Summary', rows.summary, subtitle);
  }
  const name = eventsToExport.length === 1 ? eventDisplayName(eventsToExport[0], lang) : 'horse-shows';
  await saveStyledWorkbook(workbook, `${fileSafeName(name)}-${today}.xlsx`);
  return true;
}

function tableHtml(rows, emptyText = '-') {
  if (!rows.length) return `<p class="empty">${escapeHtml(emptyText)}</p>`;
  const headers = Object.keys(rows[0]);
  return `
    <table>
      <thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr></thead>
      <tbody>
        ${rows.map((row) => `<tr>${headers.map((header) => `<td>${escapeHtml(row[header])}</td>`).join('')}</tr>`).join('')}
      </tbody>
    </table>
  `;
}

function compactTableHtml(rows, columns, emptyText = '-') {
  if (!rows.length) return `<p class="empty">${escapeHtml(emptyText)}</p>`;
  return `
    <table>
      <thead><tr>${columns.map((column) => `<th class="${column.className || ''}">${escapeHtml(column.label)}</th>`).join('')}</tr></thead>
      <tbody>
        ${rows
          .map((row) => `<tr>${columns.map((column) => `<td class="${column.className || ''}">${escapeHtml(column.value(row))}</td>`).join('')}</tr>`)
          .join('')}
      </tbody>
    </table>
  `;
}

function detailGridHtml(items) {
  const visible = items.filter((item) => item.value !== undefined && item.value !== null && String(item.value).trim() !== '');
  if (!visible.length) return '';
  return `
    <div class="detail-grid">
      ${visible
        .map(
          (item) => `
            <div>
              <span>${escapeHtml(item.label)}</span>
              <strong>${escapeHtml(item.value)}</strong>
            </div>
          `
        )
        .join('')}
    </div>
  `;
}

function eventInfoReportHtml(event, lang, L) {
  return detailGridHtml([
    { label: L.category, value: categoryName(event, lang) },
    { label: L.city, value: event.city || '-' },
    { label: L.start, value: fmtDate(event.start_date) },
    { label: L.end, value: fmtDate(event.end_date) },
    { label: L.organizer, value: event.organizer || '-' },
    { label: L.notes, value: event.notes || '' },
  ]);
}

function teamReportHtml(event, L) {
  const rows = (event.team_members || []).map((member) => ({
    [L.type]: member.member_role === 'Trainer' ? L.trainers : L.grooms,
    [L.personName]: member.member_display_name || member.member_name || '-',
  }));
  return tableHtml(rows);
}

function classEntriesReportHtml(event, lang, L, options) {
  const classes = event.classes || [];
  if (!classes.length) return `<p class="empty">-</p>`;

  const entryColumns = [
    { label: L.catalog, value: (entry) => entry.catalogue_number || '-' },
    { label: L.horse, value: (entry) => horseName(entry, lang) },
    { label: L.owner, value: (entry) => entry.owner_name || '-' },
    { label: L.handler, value: (entry) => entry.handler_display_name || entry.handler_name || '-' },
    { label: L.rank, value: (entry) => entry.class_rank || '-' },
    ...(options.scores
      ? [
          { label: L.score, value: (entry) => entry.score_total || '-' },
          {
            label: L.points,
            className: 'score-line',
            value: (entry) =>
              [
                entry.score_head_neck && `${L.headNeck}: ${entry.score_head_neck}`,
                entry.score_body && `${L.body}: ${entry.score_body}`,
                entry.score_legs && `${L.legs}: ${entry.score_legs}`,
                entry.score_movement && `${L.movement}: ${entry.score_movement}`,
              ]
                .filter(Boolean)
                .join(' | ') || '-',
          },
        ]
      : []),
    { label: L.qualified, value: (entry) => (Number(entry.qualified_for_championship) ? L.qualified : '-') },
  ];

  return classes
    .map((showClass) => {
      const entries = (event.entries || []).filter((entry) => Number(entry.class_id) === Number(showClass.class_id));
      return `
        <div class="class-block">
          <div class="class-head">
            <div>
              <h4>${escapeHtml(className(showClass, lang))}</h4>
              <p>${escapeHtml(
                [
                  showClass.class_code && `${L.classCode}: ${showClass.class_code}`,
                  showClass.class_date && `${L.classDate}: ${fmtDate(showClass.class_date)}`,
                  showClass.start_time && `${L.classTime}: ${showClass.start_time}`,
                  showClass.championship_group && `${L.group}: ${showClass.championship_group}`,
                  showClass.sex && `${L.sex}: ${showClass.sex}`,
                ]
                  .filter(Boolean)
                  .join(' · ')
              )}</p>
            </div>
            <strong>${entries.length}</strong>
          </div>
          ${compactTableHtml(entries, entryColumns)}
        </div>
      `;
    })
    .join('');
}

function championshipResultsReportHtml(event, lang, L, options) {
  const championships = event.championships || [];
  if (!championships.length) return `<p class="empty">-</p>`;

  const resultColumns = [
    { label: L.horse, value: (result) => horseName(result, lang) },
    { label: L.handler, value: (result) => result.handler_display_name || result.handler_name || '-' },
    { label: L.medal, value: (result) => labelForMedal(result.medal, L) },
    { label: L.rank, value: (result) => result.final_rank || '-' },
    ...(options.scores
      ? [
          {
            label: L.points,
            className: 'score-line',
            value: (result) =>
              [
                result.championship_points && `${L.points}: ${result.championship_points}`,
                result.gold_votes && `${L.goldVotes}: ${result.gold_votes}`,
                result.silver_votes && `${L.silverVotes}: ${result.silver_votes}`,
                result.bronze_votes && `${L.bronzeVotes}: ${result.bronze_votes}`,
              ]
                .filter(Boolean)
                .join(' | ') || '-',
          },
        ]
      : []),
    { label: L.notes, value: (result) => result.notes || '-' },
  ];

  return championships
    .map((championship) => {
      const results = (event.championship_results || []).filter(
        (result) => Number(result.championship_id) === Number(championship.championship_id)
      );
      return `
        <div class="class-block">
          <div class="class-head">
            <div>
              <h4>${escapeHtml(championshipName(championship, lang))}</h4>
              <p>${escapeHtml([championship.championship_group && `${L.group}: ${championship.championship_group}`].filter(Boolean).join(' · '))}</p>
            </div>
            <strong>${results.length}</strong>
          </div>
          ${compactTableHtml(results, resultColumns)}
        </div>
      `;
    })
    .join('');
}

function awardsReportHtml(event, lang, L) {
  const rows = (event.awards || []).map((award) => {
    const showClass = (event.classes || []).find((row) => Number(row.class_id) === Number(award.class_id));
    const championship = (event.championships || []).find((row) => Number(row.championship_id) === Number(award.championship_id));
    return {
      [L.awardScope]: award.award_scope === 'Championship' ? L.championshipAward : award.award_scope === 'Event' ? L.eventName : L.classAward,
      [L.className]: award.award_scope === 'Championship' ? championshipName(championship, lang) : className(showClass || {}, lang),
      [L.awardName]: lang === 'ar' ? award.award_name_ar || award.award_name || '-' : award.award_name || award.award_name_ar || '-',
      [L.horse]: horseName(award, lang),
      [L.rank]: award.placement || '-',
      [L.prize]: moneyText(award.prize_amount, award.prize_currency),
      [L.recipient]: award.prize_recipient === 'Stable' ? L.stablePays : L.ownerPays,
      [L.notes]: award.notes || '-',
    };
  });
  return tableHtml(rows);
}

function exportEventsPdf(eventsToExport, lang, L, options = DEFAULT_REPORT_OPTIONS, systemSettings = {}) {
  if (!eventsToExport.length) return false;
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const title = eventsToExport.length === 1 ? eventDisplayName(eventsToExport[0], lang) : L.title;
  const documentTitle = L.title;
  const generatedAt = formatDateTimeDisplay(new Date());
  const systemName = systemSettings.system_name || 'Smart Stable';
  const logoUrl = resolveAssetUrl(systemSettings.logo);
  const logoHtml = logoUrl
    ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="${escapeHtml(systemName)}" />`
    : `<div class="logo-fallback">${escapeHtml(systemName.slice(0, 1))}</div>`;
  const eventLogoHtml = logoUrl
    ? `<img class="event-logo" src="${escapeHtml(logoUrl)}" alt="${escapeHtml(systemName)}" />`
    : `<div class="event-logo-fallback">${escapeHtml(systemName.slice(0, 1))}</div>`;
  const eventSections = eventsToExport.map((event) => {
    const eventName = eventDisplayName(event, lang);
    return `
      <section class="event">
        <div class="event-head">
          <div class="event-title-row">
            ${eventLogoHtml}
            <div>
              <h2>${escapeHtml(eventName)}</h2>
              <p>${escapeHtml([fmtDate(event.start_date), fmtDate(event.end_date)].filter(Boolean).join(' · '))}</p>
            </div>
          </div>
          <div class="badge">${escapeHtml(event.organizer || '-')}</div>
        </div>
        ${options.summary ? eventInfoReportHtml(event, lang, L) : ''}
        <div class="metrics">
          <div><span>${escapeHtml(L.classes)}</span><strong>${event.classes?.length || 0}</strong></div>
          <div><span>${escapeHtml(L.entries)}</span><strong>${event.entries?.length || 0}</strong></div>
          <div><span>${escapeHtml(L.championships)}</span><strong>${event.championships?.length || 0}</strong></div>
          <div><span>${escapeHtml(L.addAward)}</span><strong>${event.awards?.length || 0}</strong></div>
        </div>
        ${options.team ? `<h3>${escapeHtml(L.trainers)} / ${escapeHtml(L.grooms)}</h3>${teamReportHtml(event, L)}` : ''}
        ${options.entries ? `<h3>${escapeHtml(L.classes)} / ${escapeHtml(L.entries)}</h3>${classEntriesReportHtml(event, lang, L, options)}` : ''}
        ${options.championships ? `<h3>${escapeHtml(L.championships)}</h3>${championshipResultsReportHtml(event, lang, L, options)}` : ''}
        ${options.awards ? `<h3>${escapeHtml(L.addAward)}</h3>${awardsReportHtml(event, lang, L)}` : ''}
      </section>
    `;
  }).join('');

  const reportWindow = window.open('', '_blank');
  if (!reportWindow) return false;
  reportWindow.document.write(`
    <!doctype html>
    <html dir="${dir}" lang="${lang}">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          @page { size: A4; margin: 13mm; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: "Segoe UI", Tahoma, Arial, sans-serif;
            color: #454b54;
            background: #f7f3ea;
          }
          .report {
            max-width: 980px;
            margin: 0 auto;
            padding: 18px;
          }
          .cover {
            border: 1px solid #dfd1b4;
            background: linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%);
            padding: 14px 18px;
            margin-bottom: 12px;
          }
          .brand-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }
          .brand {
            display: flex;
            align-items: center;
            gap: 14px;
          }
          .logo {
            width: 58px;
            height: 58px;
            object-fit: contain;
            background: #fff;
            border: 1px solid #eadfc9;
            padding: 6px;
          }
          .logo-fallback {
            width: 58px;
            height: 58px;
            display: grid;
            place-items: center;
            border: 1px solid #eadfc9;
            background: #efe4cc;
            color: #a8893e;
            font-weight: 900;
            font-size: 22px;
          }
          h1, h2, h3 { margin: 0; color: #454b54; }
          h1 { font-size: 22px; }
          h2 { font-size: 20px; }
          h3 { font-size: 15px; margin: 18px 0 8px; }
          h4 { margin: 0; font-size: 14px; color: #454b54; }
          p { margin: 6px 0 0; color: #6f7782; }
          .meta {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin-top: 12px;
          }
          .meta div, .metrics div {
            border: 1px solid #eadfc9;
            background: #fffdfa;
            padding: 12px;
          }
          .meta span, .metrics span {
            display: block;
            color: #a8893e;
            font-size: 11px;
            font-weight: 800;
            margin-bottom: 5px;
          }
          .meta strong, .metrics strong {
            font-size: 18px;
            color: #454b54;
          }
          .detail-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin: 14px 0;
          }
          .detail-grid div {
            border: 1px solid #eadfc9;
            background: #fff;
            padding: 9px 10px;
          }
          .detail-grid span {
            display: block;
            color: #8d6d2a;
            font-size: 10px;
            font-weight: 900;
            margin-bottom: 4px;
          }
          .detail-grid strong {
            display: block;
            color: #454b54;
            font-size: 12px;
            line-height: 1.45;
            font-weight: 800;
          }
          .event {
            break-inside: auto;
            border: 1px solid #dfd1b4;
            background: #fffdfa;
            margin: 16px 0;
            padding: 18px;
          }
          .event-head {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            align-items: flex-start;
          }
          .event-title-row {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .event-logo, .event-logo-fallback {
            width: 42px;
            height: 42px;
            border: 1px solid #eadfc9;
            background: #fff;
            object-fit: contain;
            padding: 4px;
            flex: 0 0 auto;
          }
          .event-logo-fallback {
            display: grid;
            place-items: center;
            color: #a8893e;
            font-weight: 900;
          }
          .badge {
            border: 1px solid #b79b55;
            color: #8d6d2a;
            padding: 7px 12px;
            font-weight: 800;
            white-space: nowrap;
          }
          .metrics {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin: 14px 0;
          }
          .class-block {
            border: 1px solid #eadfc9;
            background: #fff;
            margin: 10px 0 16px;
            break-inside: avoid;
          }
          .class-head {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 14px;
            background: #f4efe5;
            border-bottom: 1px solid #dfd1b4;
            border-top: 2px solid #b79b55;
            padding: 10px 12px;
          }
          .class-head strong {
            min-width: 38px;
            height: 30px;
            display: grid;
            place-items: center;
            background: #fffdfa;
            border: 1px solid #dfd1b4;
            color: #8d6d2a;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
            background: #fff;
            font-size: 9.5px;
          }
          th {
            background: #f4efe5;
            color: #454b54;
            border-bottom: 1px solid #dfd1b4;
            text-align: ${dir === 'rtl' ? 'right' : 'left'};
            padding: 8px;
            font-weight: 900;
          }
          td {
            border-bottom: 1px solid #eadfc9;
            padding: 7px 8px;
            vertical-align: top;
            word-break: break-word;
          }
          .score-line {
            white-space: nowrap;
            font-size: 8.6px;
          }
          .empty {
            border: 1px solid #eadfc9;
            background: #fff;
            padding: 10px;
          }
          @media print {
            body { background: #fff; }
            .report { padding: 0; }
            .event { page-break-inside: auto; }
            .class-block { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <main class="report">
          <section class="cover">
            <div class="brand-row">
              <div class="brand">
                ${logoHtml}
                <div>
                  <h2>${escapeHtml(systemName)}</h2>
                  <p>${escapeHtml(L.title)}</p>
                </div>
              </div>
            </div>
            <h1>${escapeHtml(documentTitle)}</h1>
            <div class="meta">
              <div><span>${escapeHtml(L.reportGeneratedAt)}</span><strong>${escapeHtml(generatedAt)}</strong></div>
              <div><span>${escapeHtml(L.entries)}</span><strong>${eventsToExport.reduce((sum, event) => sum + (event.entries?.length || 0), 0)}</strong></div>
              <div><span>${escapeHtml(L.championships)}</span><strong>${eventsToExport.reduce((sum, event) => sum + (event.championships?.length || 0), 0)}</strong></div>
            </div>
          </section>
          ${eventSections}
        </main>
        <script>
          window.onload = function () {
            window.focus();
            setTimeout(function () { window.print(); }, 450);
          };
        </script>
      </body>
    </html>
  `);
  reportWindow.document.close();
  return true;
}

export default function HorseCompetitionsPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const L = tLabels(lang);
  // صلاحية الإضافة/التعديل القابلة للتخصيص (الاطلاع = صلاحية الصفحة competitions).
  // الأدمن يتجاوز. تتحكّم بإظهار أزرار الإنشاء/التعديل/الحذف (والخادم يفرضها أيضًا).
  const canEdit = hasActionPermission('competitions-edit');
  const [events, setEvents] = useState([]);
  const [horses, setHorses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [people, setPeople] = useState({ trainers: [], grooms: [], handlers: [] });
  const [systemSettings, setSystemSettings] = useState({});
  const [filters, setFilters] = useState({ from: yearStart, to: today, q: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reportDialog, setReportDialog] = useState(null);
  const [reportOptions, setReportOptions] = useState(DEFAULT_REPORT_OPTIONS);
  const [personDialog, setPersonDialog] = useState(null);
  const [personForm, setPersonForm] = useState({ name: '' });
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const selected = useMemo(
    () => events.find((event) => Number(event.event_id) === Number(selectedId)) || events[0] || null,
    [events, selectedId]
  );

  const fetchAll = useCallback(async () => {
    setError('');
    try {
      const [eventsRes, horsesRes, templatesRes, categoriesRes, peopleRes, settingsRes] = await Promise.all([
        api.get('/competitions/events', { params: filters }),
        api.get('/horseslist', { params: { is_deleted: 0, is_active: 1 } }),
        api.get('/competitions/classifications'),
        api.get('/competitions/show-categories'),
        api.get('/competitions/people'),
        api.get('/system-settings').catch(() => ({ data: {} })),
      ]);
      const nextEvents = eventsRes.data?.rows || [];
      setEvents(nextEvents);
      setHorses(Array.isArray(horsesRes.data) ? horsesRes.data : horsesRes.data?.rows || []);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setPeople(peopleRes.data || { trainers: [], grooms: [], handlers: [] });
      setSystemSettings(settingsRes.data || {});
      if (!selectedId && nextEvents[0]) setSelectedId(nextEvents[0].event_id);
    } catch (err) {
      setError(err.response?.data?.message || L.loadError);
    }
  }, [filters, selectedId, L.loadError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openDialog = (type, seed = {}) => {
    const defaults = {
      event: blankEvent,
      class: blankClass,
      entry: blankEntry,
      entryResult: blankEntryResult,
      award: blankAward,
      championship: blankChampionship,
      result: blankResult,
      category: blankCategory,
    };
    setDialog(type);
    setForm({ ...copy(defaults[type]), ...seed });
  };

  const entriesForClass = (classId) =>
    (selected?.entries || []).filter((entry) => Number(entry.class_id) === Number(classId));

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setClassScoreField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      const total = classScoreFields.reduce((sum, key) => sum + toNumber(next[key]), 0);
      return { ...next, score_total: total ? total.toFixed(2) : '' };
    });
  };

  const setChampionshipVoteField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      const total = toNumber(next.gold_votes) * 4 + toNumber(next.silver_votes) * 2 + toNumber(next.bronze_votes);
      return { ...next, championship_points: total ? String(total) : '' };
    });
  };

  const save = async () => {
    try {
      setError('');
      setMessage('');
      const eventPayload = dialog === 'event'
        ? {
            ...form,
            team_members: [
              ...(form.trainers || []).map((item) => toTeamMember(item, 'Trainer')).filter(Boolean),
              ...(form.grooms || []).map((item) => toTeamMember(item, 'Groom')).filter(Boolean),
            ],
          }
        : form;
      if (dialog === 'event' && form.event_id) await api.put(`/competitions/events/${form.event_id}`, eventPayload);
      else if (dialog === 'event') await api.post('/competitions/events', eventPayload);
      if (dialog === 'class' && form.class_id) await api.put(`/competitions/classes/${form.class_id}`, form);
      else if (dialog === 'class') await api.post(`/competitions/events/${selected.event_id}/classes`, form);
      if (dialog === 'entry' && form.entry_id) await api.put(`/competitions/entries/${form.entry_id}`, form);
      else if (dialog === 'entry') await api.post(`/competitions/events/${selected.event_id}/entries`, form);
      if (dialog === 'entryResult') await api.put(`/competitions/entries/${form.entry_id}/result`, form);
      if (dialog === 'award' && form.award_id) await api.put(`/competitions/awards/${form.award_id}`, form);
      else if (dialog === 'award') await api.post(`/competitions/events/${selected.event_id}/awards`, form);
      if (dialog === 'championship') await api.post(`/competitions/events/${selected.event_id}/championships`, form);
      if (dialog === 'result' && form.result_id) await api.put(`/competitions/championship-results/${form.result_id}`, form);
      else if (dialog === 'result') await api.post(`/competitions/championships/${form.championship_id}/results`, form);
      if (dialog === 'category') await api.post('/competitions/show-categories', form);
      setDialog(null);
      setMessage(L.saveSuccess);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || L.saveError);
    }
  };

  const selectedHorse = horses.find((horse) => Number(horse.horse_id) === Number(form.horse_id)) || null;
  const championshipEntryOptions = selected?.entries || [];
  const selectedEntry = championshipEntryOptions.find((entry) => Number(entry.entry_id) === Number(form.entry_id)) || null;
  const selectedAwardEntry = (selected?.entries || []).find((entry) => Number(entry.entry_id) === Number(form.entry_id)) || null;
  const selectedHandler =
    people.handlers.find((handler) => Number(handler.id) === Number(form.handler_id)) ||
    (form.handler_name ? { type: 'Handler', name: form.handler_name } : null);

  const openPersonDialog = (type) => {
    setPersonDialog(type);
    setPersonForm({ name: '' });
  };

  const addPerson = async () => {
    try {
      const res = await api.post(`/competitions/people/${personDialog}`, personForm);
      const created = res.data;
      setPeople((prev) => ({ ...prev, [personDialog]: [...(prev[personDialog] || []), created] }));
      if (dialog === 'event' && personDialog === 'trainers') setField('trainers', [...(form.trainers || []), created]);
      if (dialog === 'event' && personDialog === 'grooms') setField('grooms', [...(form.grooms || []), created]);
      if (dialog === 'entry' && personDialog === 'handlers') {
        setForm((prev) => ({ ...prev, handler_id: created.id, handler_name: created.name }));
      }
      setPersonDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add person');
    }
  };

  const editSelectedEvent = () => {
    if (!selected) return;
    openDialog('event', eventFormFromRecord(selected));
  };

  const openClassEditor = (showClass) => {
    openDialog('class', {
      class_id: showClass.class_id,
      class_name: showClass.class_name || '',
      class_name_ar: showClass.class_name_ar || '',
      class_code: showClass.class_code || '',
      class_date: dateInputValue(showClass.class_date),
      start_time: timeInputValue(showClass.start_time),
      championship_group: showClass.championship_group || '',
      sex: showClass.sex || 'Mixed',
      min_age_months: showClass.min_age_months || '',
      max_age_months: showClass.max_age_months || '',
      sort_order: showClass.sort_order || 0,
    });
  };

  const openMedalForChampionship = (championship, seed = {}) => {
    openDialog('result', {
      championship_id: championship.championship_id,
      medal: 'Gold',
      final_rank: '',
      handler_fee_paid_by: 'Owner',
      prize_received_by: 'Owner',
      ...seed,
    });
  };

  const openClassResult = (entry) => {
    openDialog('entryResult', {
      entry_id: entry.entry_id,
      class_rank: entry.class_rank || '',
      qualified_for_championship: Number(entry.qualified_for_championship || 0),
      score_type: entry.score_type || '',
      score_head_neck: entry.score_head_neck || '',
      score_body: entry.score_body || '',
      score_legs: entry.score_legs || '',
      score_movement: entry.score_movement || '',
      score_total: entry.score_total || '',
      result_notes: entry.result_notes || '',
    });
  };

  const openEntryEditor = (entry) => {
    openDialog('entry', {
      entry_id: entry.entry_id,
      class_id: entry.class_id || '',
      horse_id: entry.horse_id || '',
      catalogue_number: entry.catalogue_number || '',
      handler_id: entry.handler_id || '',
      handler_name: entry.handler_display_name || entry.handler_name || '',
      handler_fee: entry.handler_fee || '',
      handler_fee_paid_by: entry.handler_fee_paid_by || 'Owner',
      registration_fee_amount: entry.registration_fee_amount || '',
      registration_fee_currency: entry.registration_fee_currency || 'SAR',
      registration_fee_payer: entry.registration_fee_payer || 'Owner',
      result_notes: entry.result_notes || '',
    });
  };

  const openAwardDialog = (seed) => {
    openDialog('award', {
      ...blankAward,
      ...seed,
    });
  };

  const editAward = (award) => {
    openAwardDialog({
      award_id: award.award_id,
      award_scope: award.award_scope || 'Class',
      class_id: award.class_id || '',
      championship_id: award.championship_id || '',
      entry_id: award.entry_id || '',
      championship_result_id: award.championship_result_id || '',
      award_name: award.award_name || '',
      award_name_ar: award.award_name_ar || '',
      placement: award.placement || '',
      prize_amount: award.prize_amount || '',
      prize_currency: award.prize_currency || 'SAR',
      prize_recipient: award.prize_recipient || 'Owner',
      notes: award.notes || '',
    });
  };

  const deleteAward = async (awardId) => {
    try {
      await api.delete(`/competitions/awards/${awardId}`);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete award');
    }
  };

  const deleteClass = async (classId) => {
    if (!window.confirm(lang === 'ar' ? 'حذف هذا الشوط وكل مدخلاته؟' : 'Delete this class and all its entries?')) return;
    try {
      await api.delete(`/competitions/classes/${classId}`);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete class');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm(lang === 'ar' ? 'حذف هذا المدخل (الخيل) من المسابقة؟' : 'Delete this entry (horse) from the show?')) return;
    try {
      await api.delete(`/competitions/entries/${entryId}`);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete entry');
    }
  };

  const copyEntryToChampionship = async (entry) => {
    try {
      const cls = (selected?.classes || []).find((item) => item.class_id === entry.class_id);
      const group = cls?.championship_group || className(cls || {}, lang);
      if (!group || group === '-') {
        setError(L.missingChampionshipGroup);
        return;
      }

      const existing = (selected?.championships || []).find((championship) => championship.championship_group === group);
      let championshipId = existing?.championship_id;

      if (!championshipId) {
        const res = await api.post(`/competitions/events/${selected.event_id}/championships/ensure`, {
          championship_group: group,
          championship_name: `${group} Championship`,
          championship_name_ar: `${group}`,
        });
        championshipId = res.data?.championship_id;
        await fetchAll();
      }

      await api.post(`/competitions/championships/${championshipId}/entries`, {
        entry_id: entry.entry_id,
      });
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || L.copyChampionshipError);
    }
  };

  const openReportDialog = (scope, format) => {
    setReportDialog({ scope, format });
    setError('');
  };

  const toggleReportOption = (key) => {
    setReportOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const runReportExport = async () => {
    const reportEvents = reportDialog?.scope === 'selected' ? (selected ? [selected] : []) : events;
    if (!reportEvents.length) {
      setError(L.noEvents);
      return;
    }

    const ok = reportDialog?.format === 'xlsx'
      ? await exportEventsExcel(reportEvents, lang, L, reportOptions)
      : exportEventsPdf(reportEvents, lang, L, reportOptions, systemSettings);
    if (!ok) setError(L.noEvents);
    else setReportDialog(null);
  };

  /*
  const copyEntryToChampionshipOld = (entry, medal = 'Gold') => {
    const matchingChampionship = (selected?.championships || []).find((championship) => {
      const cls = (selected?.classes || []).find((item) => item.class_id === entry.class_id);
      return cls?.championship_group && cls.championship_group === championship.championship_group;
    }) || (selected?.championships || [])[0];

    openDialog('result', {
      championship_id: matchingChampionship?.championship_id || '',
      entry_id: entry.entry_id,
      medal,
      final_rank: medal === 'Gold' ? 1 : medal === 'Silver' ? 2 : medal === 'Bronze' ? 3 : '',
    });
  };
  */

  return (
    <Box className="smart-page-container" sx={{ px: { xs: 2, md: 0 }, py: 3 }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: COLORS.text }}>
              {L.title}
            </Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{L.hint}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button startIcon={<RefreshIcon />} onClick={fetchAll} variant="outlined" sx={{ borderColor: COLORS.line, color: COLORS.text }}>
              {L.refresh}
            </Button>
            <Button
              startIcon={<PictureAsPdfIcon />}
              onClick={() => openReportDialog('all', 'pdf')}
              variant="outlined"
              disabled={!events.length}
              sx={{ borderColor: COLORS.line, color: COLORS.text }}
            >
              {L.exportAllPdf}
            </Button>
            <Button
              startIcon={<TableChartIcon />}
              onClick={() => openReportDialog('all', 'xlsx')}
              variant="outlined"
              disabled={!events.length}
              sx={{ borderColor: COLORS.line, color: COLORS.text }}
            >
              {L.exportAllExcel}
            </Button>
            {canEdit && (
              <Button startIcon={<AddIcon />} onClick={() => openDialog('event')} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
                {L.addEvent}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
      {message ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert> : null}

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField label={L.from} type="date" size="small" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label={L.to} type="date" size="small" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label={L.search} size="small" value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} sx={{ minWidth: { md: 320 } }} />
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '340px 1fr' }, gap: 2 }}>
        <Paper elevation={0} sx={{ ...cardSx, p: 1.5, alignSelf: 'start' }}>
          <Stack spacing={1}>
            {events.length === 0 ? <Typography sx={{ color: COLORS.muted, p: 2 }}>{L.noEvents}</Typography> : null}
            {events.map((event) => (
              <Button
                key={event.event_id}
                onClick={() => setSelectedId(event.event_id)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'start',
                  color: COLORS.text,
                  bgcolor: Number(selected?.event_id) === Number(event.event_id) ? COLORS.goldSoft : 'transparent',
                  border: `1px solid ${Number(selected?.event_id) === Number(event.event_id) ? COLORS.gold : 'transparent'}`,
                  p: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900 }}>{event.event_name}</Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                    <span className="date-ltr">{fmtDate(event.start_date)}</span> · {event.city || '-'}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Stack>
        </Paper>

        <Stack spacing={2}>
          {selected ? (
            <>
              <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{selected.event_name}</Typography>
                    <Typography sx={{ color: COLORS.muted }}>
                      <span className="date-ltr">{fmtDate(selected.start_date)}</span> - <span className="date-ltr">{fmtDate(selected.end_date)}</span> · {selected.city || '-'} · {categoryName(selected, lang)}
                    </Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                      {[selected.start_time, selected.end_time && `- ${selected.end_time}`, selected.event_timezone].filter(Boolean).join(' ')}
                    </Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                      {[selected.event_address, selected.contact_phone].filter(Boolean).join(' · ')}
                    </Typography>
                    {(selected.team_members || []).length ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                        {(selected.team_members || []).map((member) => (
                          <Chip
                            key={member.team_id}
                            size="small"
                            label={`${member.member_role === 'Trainer' ? L.trainers : L.grooms}: ${member.member_display_name || member.member_name || '-'}`}
                            sx={{ bgcolor: COLORS.cardSoft, color: COLORS.text }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button startIcon={<PictureAsPdfIcon />} onClick={() => openReportDialog('selected', 'pdf')} variant="outlined" sx={{ borderColor: COLORS.line, color: COLORS.text }}>
                      {L.exportPdf}
                    </Button>
                    <Button startIcon={<TableChartIcon />} onClick={() => openReportDialog('selected', 'xlsx')} variant="outlined" sx={{ borderColor: COLORS.line, color: COLORS.text }}>
                      {L.exportExcel}
                    </Button>
                    {canEdit && <Button startIcon={<EditIcon />} onClick={editSelectedEvent}>{L.editEvent}</Button>}
                    {canEdit && <Button startIcon={<AddIcon />} onClick={() => openDialog('class')}>{L.addClass}</Button>}
                  </Stack>
                </Stack>
              </Paper>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 }}>
                <MetricCard title={L.classes} value={selected.class_count || selected.classes?.length || 0} icon={<ScoreboardIcon />} />
                <MetricCard title={L.entries} value={selected.entry_count || selected.entries?.length || 0} icon={<EmojiEventsIcon />} color={COLORS.blue} soft={COLORS.blueSoft} />
                <MetricCard title={L.championships} value={selected.championship_count || selected.championships?.length || 0} icon={<MilitaryTechIcon />} color={COLORS.green} soft={COLORS.greenSoft} />
                <MetricCard title={L.medals} value={selected.championship_results?.length || 0} icon={<MilitaryTechIcon />} color={COLORS.rose} soft={COLORS.roseSoft} />
                <MetricCard title={L.addAward} value={selected.awards?.length || 0} icon={<EmojiEventsIcon />} color={COLORS.warning} soft={COLORS.warningSoft} />
              </Box>

              <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{L.classes}</Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>{L.classesHint}</Typography>
                  </Box>
                  {canEdit && (
                    <Button startIcon={<AddIcon />} onClick={() => openDialog('class')}>
                      {L.addClass}
                    </Button>
                  )}
                </Stack>

                {(selected.classes || []).length === 0 ? (
                  <Alert severity="info" sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text }}>
                    {L.noClasses}
                  </Alert>
                ) : null}

                <Stack spacing={2}>
                  {(selected.classes || []).map((showClass) => {
                    const classEntries = entriesForClass(showClass.class_id);
                    return (
                      <Paper key={showClass.class_id} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: COLORS.cardSoft }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <Box>
                              <Typography sx={{ fontWeight: 900 }}>{className(showClass, lang)}</Typography>
                              <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                                {showClass.class_code ? `${showClass.class_code} · ` : ''}
                                {showClass.class_date ? <span className="date-ltr">{fmtDate(showClass.class_date)}</span> : null}
                                {[showClass.start_time, showClass.championship_group, showClass.sex].filter(Boolean).length
                                  ? ` · ${[showClass.start_time, showClass.championship_group, showClass.sex].filter(Boolean).join(' · ')}`
                                  : ''}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                              {canEdit && (
                                <>
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => openClassEditor(showClass)}
                                  >
                                    {L.editClass}
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => openDialog('entry', { class_id: showClass.class_id })}
                                  >
                                    {L.addHorseToClass}
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => deleteClass(showClass.class_id)}
                                  >
                                    {lang === 'ar' ? 'حذف الشوط' : 'Delete class'}
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                        <Box sx={{ p: 1.5 }}>
                          {classEntries.length === 0 ? (
                            <Typography sx={{ color: COLORS.muted, py: 1 }}>-</Typography>
                          ) : null}
                          <Stack spacing={1}>
                            {classEntries.map((entry) => (
                              <Paper
                                key={entry.entry_id}
                                elevation={0}
                                sx={{
                                  border: `1px solid ${COLORS.line}`,
                                  borderRadius: 2,
                                  bgcolor: COLORS.card,
                                  p: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                      xs: '1fr',
                                      md: '80px minmax(180px, 1.5fr) 90px 150px 130px auto',
                                    },
                                    gap: 1.5,
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>{L.catalog}</Typography>
                                    <Typography sx={{ fontWeight: 900 }}>{entry.catalogue_number || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography sx={{ fontWeight: 900 }}>{horseName(entry, lang)}</Typography>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>{entry.owner_name || '-'}</Typography>
                                    {entry.handler_display_name || entry.handler_name ? (
                                      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                                        {L.handler}: {entry.handler_display_name || entry.handler_name}
                                      </Typography>
                                    ) : null}
                                    {Number(entry.handler_fee) > 0 ? (
                                      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                                        {L.handlerFee}: {formatter.format(entry.handler_fee)} SAR · {entry.handler_fee_paid_by === 'Stable' ? L.stablePays : L.ownerPays}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                  <Box>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>{L.rank}</Typography>
                                    <Typography sx={{ fontWeight: 900 }}>{entry.class_rank || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>{L.score}</Typography>
                                    <Typography sx={{ fontWeight: 900 }}>{entry.score_total ? formatter.format(entry.score_total) : '-'}</Typography>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                                      {[
                                        entry.score_type && `${L.type}: ${entry.score_type}`,
                                        entry.score_movement && `${L.movement}: ${entry.score_movement}`,
                                      ].filter(Boolean).join(' · ')}
                                    </Typography>
                                    {Number(entry.registration_fee_amount) > 0 ? (
                                      <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                                        {L.registrationFee}: {formatter.format(entry.registration_fee_amount)} {entry.registration_fee_currency || 'SAR'} · {entry.registration_fee_payer === 'Stable' ? L.stablePays : L.ownerPays}
                                      </Typography>
                                    ) : null}
                                    {(selected.awards || []).filter((award) => award.award_scope === 'Class' && Number(award.entry_id) === Number(entry.entry_id)).map((award) => (
                                      <Stack key={award.award_id} direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Chip
                                          size="small"
                                          label={`${award.award_name}${Number(award.prize_amount) > 0 ? ` · ${formatter.format(award.prize_amount)} ${award.prize_currency}` : ''}`}
                                          sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text }}
                                        />
                                        <IconButton size="small" onClick={() => editAward(award)} sx={{ color: COLORS.gold }}>
                                          <EditIcon fontSize="inherit" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => deleteAward(award.award_id)} sx={{ color: COLORS.rose }}>
                                          <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                      </Stack>
                                    ))}
                                  </Box>
                                  <Box>
                                    <Chip size="small" label={Number(entry.qualified_for_championship) ? L.qualified : '-'} sx={{ bgcolor: Number(entry.qualified_for_championship) ? COLORS.greenSoft : COLORS.cardSoft }} />
                                  </Box>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ md: 'flex-end' }}>
                                    {canEdit && (
                                    <>
                                    <Button size="small" onClick={() => openEntryEditor(entry)}>
                                      {L.editEntry}
                                    </Button>
                                    <Button size="small" onClick={() => openClassResult(entry)}>
                                      {L.recordClassResult}
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        const existingAward = (selected.awards || []).find(
                                          (award) => award.award_scope === 'Class' && Number(award.entry_id) === Number(entry.entry_id) && Number(award.class_id) === Number(entry.class_id)
                                        );
                                        if (existingAward) editAward(existingAward);
                                        else openAwardDialog({
                                          award_scope: 'Class',
                                          class_id: entry.class_id,
                                          entry_id: entry.entry_id,
                                          award_name: className(showClass, lang),
                                          placement: entry.class_rank || '',
                                        });
                                      }}
                                    >
                                      {L.addAward}
                                    </Button>
                                    {Number(entry.qualified_for_championship) ? (
                                      <Button size="small" onClick={() => copyEntryToChampionship(entry)} sx={{ color: COLORS.gold }}>
                                        {L.copyToChampionship}
                                      </Button>
                                    ) : null}
                                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteEntry(entry.entry_id)}>
                                      {lang === 'ar' ? 'حذف' : 'Delete'}
                                    </Button>
                                    </>
                                    )}
                                  </Stack>
                                </Box>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>{L.championships}</Typography>
                <Stack spacing={2}>
                  {(selected.championships || []).map((championship) => {
                    const results = (selected.championship_results || []).filter(
                      (result) => Number(result.championship_id) === Number(championship.championship_id)
                    );
                    return (
                      <Paper key={championship.championship_id} elevation={0} sx={{ ...cardSx, p: 2, bgcolor: COLORS.cardSoft }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>{championship.championship_name}</Typography>
                            <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>{championship.championship_group || '-'}</Typography>
                          </Box>
                          <Button
                            size="small"
                            startIcon={<MilitaryTechIcon />}
                            onClick={() => openMedalForChampionship(championship)}
                          >
                            {L.addMedalHere}
                          </Button>
                        </Stack>
                        <Box sx={{ mt: 1.5 }}>
                          {results.length === 0 ? (
                            <Typography sx={{ color: COLORS.muted, py: 1 }}>-</Typography>
                          ) : null}
                          <Stack spacing={1}>
                            {results.map((result) => (
                              <Paper
                                key={result.result_id}
                                elevation={0}
                                sx={{
                                  border: `1px solid ${COLORS.line}`,
                                  borderRadius: 2,
                                  bgcolor: COLORS.card,
                                  p: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                      xs: '1fr',
                                      md: 'minmax(220px, 1.3fr) 160px 160px 130px auto',
                                    },
                                    gap: 1.5,
                                    alignItems: 'center',
                                  }}
                                >
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    {medalIcon(result.medal)}
                                    <Typography sx={{ fontWeight: 900 }}>{horseName(result, lang)}</Typography>
                                  </Stack>
                                  <Box>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>{L.handler}</Typography>
                                    <Typography sx={{ fontWeight: 800 }}>
                                      {result.handler_display_name || result.handler_name || '-'}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>{L.medal}</Typography>
                                    <Chip
                                      size="small"
                                      label={result.medal === 'Pending' ? L.pending : result.medal || '-'}
                                      sx={{ bgcolor: result.medal === 'Pending' ? COLORS.cardSoft : COLORS.goldSoft }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>{L.points}</Typography>
                                    <Typography sx={{ fontWeight: 900 }}>{result.championship_points}</Typography>
                                    {Number(result.handler_fee) > 0 ? (
                                      <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                                        {L.handlerFee}: {formatter.format(result.handler_fee)} SAR · {result.handler_fee_paid_by === 'Stable' ? L.stablePays : L.ownerPays}
                                      </Typography>
                                    ) : null}
                                    {Number(result.prize_amount) > 0 ? (
                                      <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                                        {L.prizeAmount}: {formatter.format(result.prize_amount)} SAR · {result.prize_received_by === 'Stable' ? L.stablePays : L.ownerPays}
                                      </Typography>
                                    ) : null}
                                    {(selected.awards || []).filter((award) => award.award_scope === 'Championship' && Number(award.championship_result_id) === Number(result.result_id)).map((award) => (
                                      <Stack key={award.award_id} direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Chip
                                          size="small"
                                          label={`${award.award_name}${Number(award.prize_amount) > 0 ? ` · ${formatter.format(award.prize_amount)} ${award.prize_currency}` : ''}`}
                                          sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text }}
                                        />
                                        <IconButton size="small" onClick={() => editAward(award)} sx={{ color: COLORS.gold }}>
                                          <EditIcon fontSize="inherit" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => deleteAward(award.award_id)} sx={{ color: COLORS.rose }}>
                                          <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                      </Stack>
                                    ))}
                                  </Box>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ md: 'flex-end' }}>
                                    {canEdit && (
                                    <>
                                    <Button
                                      size="small"
                                      onClick={() => openMedalForChampionship(championship, {
                                        result_id: result.result_id,
                                        entry_id: result.entry_id,
                                        medal: result.medal === 'Pending' ? 'Gold' : result.medal,
                                        final_rank: result.final_rank || '',
                                        gold_votes: result.gold_votes || '',
                                        silver_votes: result.silver_votes || '',
                                        bronze_votes: result.bronze_votes || '',
                                        championship_points: result.championship_points || '',
                                        handler_fee: result.handler_fee || '',
                                        handler_fee_paid_by: result.handler_fee_paid_by || 'Owner',
                                        prize_amount: result.prize_amount || '',
                                        prize_received_by: result.prize_received_by || 'Owner',
                                        notes: result.notes || '',
                                      })}
                                    >
                                      {L.setMedal}
                                    </Button>
                                    <Button size="small" onClick={() => openEntryEditor(result)}>
                                      {L.editEntry}
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        const existingAward = (selected.awards || []).find(
                                          (award) => award.award_scope === 'Championship' && Number(award.entry_id) === Number(result.entry_id) && Number(award.championship_id) === Number(championship.championship_id)
                                        );
                                        if (existingAward) editAward(existingAward);
                                        else openAwardDialog({
                                          award_scope: 'Championship',
                                          championship_id: championship.championship_id,
                                          championship_result_id: result.result_id,
                                          entry_id: result.entry_id,
                                          award_name: result.medal && result.medal !== 'Pending' ? result.medal : championship.championship_name,
                                          placement: result.final_rank || '',
                                        });
                                      }}
                                    >
                                      {L.addAward}
                                    </Button>
                                    </>
                                    )}
                                  </Stack>
                                </Box>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>
            </>
          ) : null}
        </Stack>
      </Box>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {dialog === 'event' && (form.event_id ? L.editEvent : L.addEvent)}
          {dialog === 'class' && (form.class_id ? L.editClass : L.addClass)}
          {dialog === 'entry' && (form.entry_id ? L.editEntry : L.addEntry)}
          {dialog === 'entryResult' && L.recordClassResult}
          {dialog === 'award' && (form.award_id ? L.editAward : L.addAward)}
          {dialog === 'championship' && L.addChampionship}
          {dialog === 'result' && L.addResult}
          {dialog === 'category' && L.addCategory}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: COLORS.page }}>
          {dialog === 'event' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField label={L.eventName} value={form.event_name || ''} onChange={(e) => setField('event_name', e.target.value)} required />
              <TextField label={L.organizer} value={form.organizer || ''} onChange={(e) => setField('organizer', e.target.value)} />
              <SearchCreateField
                multiple
                label={L.trainers}
                options={people.trainers}
                value={form.trainers || []}
                onChange={(_, value) => setField('trainers', value)}
                onAdd={() => openPersonDialog('trainers')}
              />
              <SearchCreateField
                multiple
                label={L.grooms}
                options={people.grooms}
                value={form.grooms || []}
                onChange={(_, value) => setField('grooms', value)}
                onAdd={() => openPersonDialog('grooms')}
              />
              <TextField select label={L.category} value={form.category_id || ''} onChange={(e) => setField('category_id', e.target.value)}>
                {categories.map((category) => (
                  <MenuItem key={category.category_id} value={category.category_id}>
                    {categoryName(category, lang)}
                  </MenuItem>
                ))}
              </TextField>
              {canEdit && (
                <Button onClick={() => openDialog('category')} startIcon={<AddIcon />}>
                  {L.addCategory}
                </Button>
              )}
              <TextField label={L.city} value={form.city || ''} onChange={(e) => setField('city', e.target.value)} />
              <TextField label={L.start} type="date" value={form.start_date || today} onChange={(e) => setField('start_date', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label={L.startTime} type="time" value={form.start_time || ''} onChange={(e) => setField('start_time', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label={L.end} type="date" value={form.end_date || today} onChange={(e) => setField('end_date', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label={L.endTime} type="time" value={form.end_time || ''} onChange={(e) => setField('end_time', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label={L.timezone} value={form.event_timezone || 'Asia/Riyadh'} onChange={(e) => setField('event_timezone', e.target.value)} />
              <TextField label={L.phone} value={form.contact_phone || ''} onChange={(e) => setField('contact_phone', e.target.value)} />
              <TextField label={L.address} value={form.event_address || ''} onChange={(e) => setField('event_address', e.target.value)} sx={{ gridColumn: { md: '1 / -1' } }} />
              <TextField label={L.notes} multiline minRows={2} value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} sx={{ gridColumn: { md: '1 / -1' } }} />
            </Box>
          ) : null}

          {dialog === 'class' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Autocomplete
                options={templates}
                getOptionLabel={(option) => lang === 'ar' ? option.template_name_ar || option.template_name_en : option.template_name_en}
                onChange={(_, value) => value && setForm((prev) => ({
                  ...prev,
                  class_name: value.template_name_en,
                  class_name_ar: value.template_name_ar || '',
                  championship_group: value.championship_group,
                  sex: value.sex,
                  class_date: prev.class_date || (selected?.start_date ? dateInputValue(selected.start_date) : ''),
                  sort_order: value.sort_order,
                }))}
                renderInput={(params) => <TextField {...params} label={L.category} />}
              />
              <TextField label={L.className} value={form.class_name || ''} onChange={(e) => setField('class_name', e.target.value)} required />
              <TextField label={`${L.className} AR`} value={form.class_name_ar || ''} onChange={(e) => setField('class_name_ar', e.target.value)} />
              <TextField label={L.classCode} value={form.class_code || ''} onChange={(e) => setField('class_code', e.target.value)} />
              <TextField label={L.classDate} type="date" value={form.class_date || ''} onChange={(e) => setField('class_date', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label={L.classTime} type="time" value={form.start_time || ''} onChange={(e) => setField('start_time', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label={L.group} value={form.championship_group || ''} onChange={(e) => setField('championship_group', e.target.value)} />
              <TextField select label={L.sex} value={form.sex || 'Mixed'} onChange={(e) => setField('sex', e.target.value)}>
                <MenuItem value="Male">{L.male}</MenuItem>
                <MenuItem value="Female">{L.female}</MenuItem>
                <MenuItem value="Mixed">{L.mixed}</MenuItem>
              </TextField>
              <TextField label={L.minAgeMonths} type="number" value={form.min_age_months || ''} onChange={(e) => setField('min_age_months', e.target.value)} />
              <TextField label={L.maxAgeMonths} type="number" value={form.max_age_months || ''} onChange={(e) => setField('max_age_months', e.target.value)} />
              <TextField label={L.sortOrder} type="number" value={form.sort_order || 0} onChange={(e) => setField('sort_order', e.target.value)} />
            </Box>
          ) : null}

          {dialog === 'entry' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Autocomplete
                options={horses}
                value={selectedHorse}
                onChange={(_, value) => setField('horse_id', value?.horse_id || '')}
                getOptionLabel={(option) => horseName(option, lang)}
                renderInput={(params) => <TextField {...params} label={L.horse} required />}
              />
              <TextField select label={L.classes} value={form.class_id || ''} onChange={(e) => setField('class_id', e.target.value)}>
                {(selected?.classes || []).map((item) => <MenuItem key={item.class_id} value={item.class_id}>{className(item, lang)}</MenuItem>)}
              </TextField>
              <TextField label={L.catalog} value={form.catalogue_number || ''} onChange={(e) => setField('catalogue_number', e.target.value)} />
              <SearchCreateField
                label={L.handler}
                options={people.handlers}
                value={selectedHandler}
                onChange={(_, value) => {
                  setForm((prev) => ({ ...prev, handler_id: value?.id || '', handler_name: value?.name || '' }));
                }}
                onAdd={() => openPersonDialog('handlers')}
              />
              <TextField label={L.handlerFee} type="number" value={form.handler_fee || ''} onChange={(e) => setField('handler_fee', e.target.value)} />
              <TextField select label={L.handlerFeePaidBy} value={form.handler_fee_paid_by || 'Owner'} onChange={(e) => setField('handler_fee_paid_by', e.target.value)}>
                <MenuItem value="Owner">{L.ownerPays}</MenuItem>
                <MenuItem value="Stable">{L.stablePays}</MenuItem>
              </TextField>
              <TextField label={L.registrationFee} type="number" value={form.registration_fee_amount || ''} onChange={(e) => setField('registration_fee_amount', e.target.value)} />
              <TextField select label={L.feePayer} value={form.registration_fee_payer || 'Owner'} onChange={(e) => setField('registration_fee_payer', e.target.value)}>
                <MenuItem value="Owner">{L.ownerPays}</MenuItem>
                <MenuItem value="Stable">{L.stablePays}</MenuItem>
              </TextField>
              <TextField label={L.resultNotes} value={form.result_notes || ''} onChange={(e) => setField('result_notes', e.target.value)} sx={{ gridColumn: { md: '1 / -1' } }} />
            </Box>
          ) : null}

          {dialog === 'entryResult' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField label={L.rank} type="number" value={form.class_rank || ''} onChange={(e) => setField('class_rank', e.target.value)} />
              <TextField select label={L.qualified} value={form.qualified_for_championship || 0} onChange={(e) => setField('qualified_for_championship', e.target.value)}>
                <MenuItem value={0}>No</MenuItem>
                <MenuItem value={1}>Yes</MenuItem>
              </TextField>
              <TextField label={L.type} type="number" value={form.score_type || ''} onChange={(e) => setClassScoreField('score_type', e.target.value)} />
              <TextField label={L.headNeck} type="number" value={form.score_head_neck || ''} onChange={(e) => setClassScoreField('score_head_neck', e.target.value)} />
              <TextField label={L.body} type="number" value={form.score_body || ''} onChange={(e) => setClassScoreField('score_body', e.target.value)} />
              <TextField label={L.legs} type="number" value={form.score_legs || ''} onChange={(e) => setClassScoreField('score_legs', e.target.value)} />
              <TextField label={L.movement} type="number" value={form.score_movement || ''} onChange={(e) => setClassScoreField('score_movement', e.target.value)} />
              <TextField label={L.total} type="number" value={form.score_total || ''} InputProps={{ readOnly: true }} />
              <TextField label={L.resultNotes} value={form.result_notes || ''} onChange={(e) => setField('result_notes', e.target.value)} />
            </Box>
          ) : null}

          {dialog === 'award' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField select label={L.awardScope} value={form.award_scope || 'Class'} onChange={(e) => setField('award_scope', e.target.value)}>
                <MenuItem value="Class">{L.classAward}</MenuItem>
                <MenuItem value="Championship">{L.championshipAward}</MenuItem>
              </TextField>
              <Autocomplete
                options={selected?.entries || []}
                value={selectedAwardEntry}
                onChange={(_, value) => setField('entry_id', value?.entry_id || '')}
                getOptionLabel={(option) => `${horseName(option, lang)}${option.catalogue_number ? ` #${option.catalogue_number}` : ''}`}
                renderInput={(params) => <TextField {...params} label={L.horse} required />}
              />
              <TextField label={L.awardName} value={form.award_name || ''} onChange={(e) => setField('award_name', e.target.value)} required />
              <TextField label={`${L.awardName} AR`} value={form.award_name_ar || ''} onChange={(e) => setField('award_name_ar', e.target.value)} />
              <TextField label={L.rank} value={form.placement || ''} onChange={(e) => setField('placement', e.target.value)} />
              <TextField label={L.prizeAmount} type="number" value={form.prize_amount || ''} onChange={(e) => setField('prize_amount', e.target.value)} />
              <TextField select label={L.prizeRecipient} value={form.prize_recipient || 'Owner'} onChange={(e) => setField('prize_recipient', e.target.value)}>
                <MenuItem value="Owner">{L.ownerPays}</MenuItem>
                <MenuItem value="Stable">{L.stablePays}</MenuItem>
              </TextField>
              <TextField label={L.notes} value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} sx={{ gridColumn: { md: '1 / -1' } }} />
            </Box>
          ) : null}

          {dialog === 'championship' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField label={L.championshipName} value={form.championship_name || ''} onChange={(e) => setField('championship_name', e.target.value)} required />
              <TextField label={`${L.championshipName} AR`} value={form.championship_name_ar || ''} onChange={(e) => setField('championship_name_ar', e.target.value)} />
              <TextField label={L.group} value={form.championship_group || ''} onChange={(e) => setField('championship_group', e.target.value)} />
            </Box>
          ) : null}

          {dialog === 'result' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField select label={L.championships} value={form.championship_id || ''} onChange={(e) => setField('championship_id', e.target.value)} required disabled={Boolean(form.result_id)}>
                {(selected?.championships || []).map((item) => <MenuItem key={item.championship_id} value={item.championship_id}>{item.championship_name}</MenuItem>)}
              </TextField>
              <Autocomplete
                options={championshipEntryOptions}
                value={selectedEntry}
                disabled={Boolean(form.result_id)}
                onChange={(_, value) => setField('entry_id', value?.entry_id || '')}
                getOptionLabel={(option) => `${horseName(option, lang)}${option.catalogue_number ? ` #${option.catalogue_number}` : ''}`}
                renderInput={(params) => <TextField {...params} label={L.horse} required />}
              />
              <TextField select label={L.medal} value={form.medal || 'Gold'} onChange={(e) => setField('medal', e.target.value)}>
                <MenuItem value="Gold">{L.gold}</MenuItem>
                <MenuItem value="Silver">{L.silver}</MenuItem>
                <MenuItem value="Bronze">{L.bronze}</MenuItem>
                <MenuItem value="Other">{L.other}</MenuItem>
                <MenuItem value="Participant">{L.participant}</MenuItem>
                <MenuItem value="Fourth">{L.fourth}</MenuItem>
              </TextField>
              <TextField label={L.rank} type="number" value={form.final_rank || ''} onChange={(e) => setField('final_rank', e.target.value)} />
              <TextField label={L.goldVotes} type="number" value={form.gold_votes || ''} onChange={(e) => setChampionshipVoteField('gold_votes', e.target.value)} />
              <TextField label={L.silverVotes} type="number" value={form.silver_votes || ''} onChange={(e) => setChampionshipVoteField('silver_votes', e.target.value)} />
              <TextField label={L.bronzeVotes} type="number" value={form.bronze_votes || ''} onChange={(e) => setChampionshipVoteField('bronze_votes', e.target.value)} />
              <TextField label={L.points} type="number" value={form.championship_points || ''} InputProps={{ readOnly: true }} />
              <TextField label={L.handlerFee} type="number" value={form.handler_fee || ''} onChange={(e) => setField('handler_fee', e.target.value)} />
              <TextField select label={L.handlerFeePaidBy} value={form.handler_fee_paid_by || 'Owner'} onChange={(e) => setField('handler_fee_paid_by', e.target.value)}>
                <MenuItem value="Owner">{L.ownerPays}</MenuItem>
                <MenuItem value="Stable">{L.stablePays}</MenuItem>
              </TextField>
              <TextField label={L.prizeAmount} type="number" value={form.prize_amount || ''} onChange={(e) => setField('prize_amount', e.target.value)} />
              <TextField select label={L.prizeReceivedBy} value={form.prize_received_by || 'Owner'} onChange={(e) => setField('prize_received_by', e.target.value)}>
                <MenuItem value="Owner">{L.ownerPays}</MenuItem>
                <MenuItem value="Stable">{L.stablePays}</MenuItem>
              </TextField>
              <TextField label={L.notes} value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} sx={{ gridColumn: { md: '1 / -1' } }} />
            </Box>
          ) : null}

          {dialog === 'category' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField label={L.categoryNameEn} value={form.category_name_en || ''} onChange={(e) => setField('category_name_en', e.target.value)} required />
              <TextField label={L.categoryNameAr} value={form.category_name_ar || ''} onChange={(e) => setField('category_name_ar', e.target.value)} />
            </Box>
          ) : null}
          <Divider sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialog(null)}>{L.cancel}</Button>
          <Button onClick={save} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>{L.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(reportDialog)} onClose={() => setReportDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{L.reportOptions}</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: COLORS.page }}>
          <Typography sx={{ color: COLORS.muted, mb: 2 }}>{L.reportOptionsHint}</Typography>
          <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
              {reportDialog?.scope === 'selected' ? L.selectedShow : L.filteredShows}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>
              {reportDialog?.format === 'xlsx' ? L.exportExcel : L.exportPdf}
            </Typography>
          </Paper>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {[
              ['summary', L.includeSummary],
              ['team', L.includeTeam],
              ['entries', L.includeEntries],
              ['championships', L.includeChampionships],
              ['awards', L.includeAwards],
              ['scores', L.includeScores],
              ['fees', L.includeFees],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={Boolean(reportOptions[key])}
                    onChange={() => toggleReportOption(key)}
                    sx={{ color: COLORS.gold, '&.Mui-checked': { color: COLORS.gold } }}
                  />
                }
                label={label}
                sx={{
                  m: 0,
                  border: `1px solid ${COLORS.line}`,
                  bgcolor: reportOptions[key] ? COLORS.greenSoft : COLORS.card,
                  px: 1,
                  py: 0.5,
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReportDialog(null)}>{L.cancel}</Button>
          <Button
            onClick={runReportExport}
            variant="contained"
            startIcon={reportDialog?.format === 'xlsx' ? <TableChartIcon /> : <PictureAsPdfIcon />}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
          >
            {L.generateReport}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(personDialog)} onClose={() => setPersonDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {personDialog === 'trainers' && L.addTrainer}
          {personDialog === 'grooms' && L.addGroom}
          {personDialog === 'handlers' && L.addHandler}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: COLORS.page }}>
          <TextField
            autoFocus
            fullWidth
            label={L.personName}
            value={personForm.name || ''}
            onChange={(e) => setPersonForm({ name: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPersonDialog(null)}>{L.cancel}</Button>
          <Button onClick={addPerson} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
            {L.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
